/**
 * ChoreographyModel — the PURE cue-evaluation engine for the Set-Piece
 * Choreography system. Given a set of cues + the current game state (player
 * position, elapsed time, fired events, time-of-day phase), resolves which
 * cues should fire and which timeline beats are due. Pure + deterministic
 * (lift-ready checklist §3): no renderer deps, no wall-clock, no global state
 * — unit-testable with fakes.
 *
 * The driver (ChoreographyDriver.ts) is the thin runtime that feeds this model
 * real inputs (camera position for region triggers, EventBus for event triggers,
 * the time-of-day clock for phase triggers) and executes the model's output
 * (emit mood events, apply fx patches). The split keeps the trigger logic
 * testable without a renderer or a running game.
 *
 * Core concepts:
 * - `CueState` — per-cue runtime state: has it fired? when? which beats are due?
 * - `evaluateCues()` — the pure entry point: given cues + state + inputs, returns
 *   a list of `FiredBeat` actions the driver should execute NOW.
 * - `once` de-dup — a cue with `once:true` fires only once per level visit.
 * - Reduce-motion degrade — 'skip' cues don't fire; 'instant' cues jump to the
 *   end state (all beats at once, no eases); 'full' runs the real timeline.
 */
import type {
  ChoreographyCue,
  CueTimelineBeat,
  EnterRegionTrigger,
  ReduceMotionMode,
} from '../../../config/atmosphere/choreography.config';
import { pointInRegion, payloadMatches, sortedTimeline, DEFAULT_REDUCE_MOTION } from '../../../config/atmosphere/choreography.config';
import type { TimeOfDayPhase } from '../../../config/atmosphere/timeOfDay.config';

/** Per-cue runtime state (the driver owns one per active cue). */
export interface CueState {
  cueId: string;
  /** Has this cue fired? (once-de-dup) */
  fired: boolean;
  /** Wall-clock ms when the cue fired (0 = not fired). The driver sets this
   *  from its own clock; the model uses it to compute beat offsets. */
  firedAtMs: number;
  /** Beats already emitted (indices into the sorted timeline). */
  emittedBeats: Set<number>;
}

/** An action the driver should execute now (a due timeline beat). */
export interface FiredBeat {
  cueId: string;
  beatIndex: number;
  beat: CueTimelineBeat;
  /** The eased progress 0..1 for `overMs` transitions (1 = instant). The
   *  driver uses this to lerp mood values. For reduce-motion 'instant', this
   *  is always 1 (jump to end). */
  progress: number;
}

/** Inputs the driver feeds the model each tick. */
export interface CueEvaluationInput {
  /** Current player position (world px) — for enterRegion triggers. The
   *  driver uses the camera center as a proxy (the camera follows the player). */
  playerX: number;
  playerY: number;
  /** Current elapsed time (ms) — the driver's monotonic clock. */
  nowMs: number;
  /** Current time-of-day phase — for timeOfDay triggers. */
  timePhase: TimeOfDayPhase;
  /** Whether reduced motion is active (affects degrade mode). */
  reduceMotion: boolean;
}

/** Create a fresh CueState for a cue (unfired, no beats emitted). */
export function createCueState(cueId: string): CueState {
  return { cueId, fired: false, firedAtMs: 0, emittedBeats: new Set() };
}

/** The effective reduce-motion mode for a cue (cue override → global default). */
export function effectiveReduceMode(cue: ChoreographyCue, reduceMotion: boolean): ReduceMotionMode {
  if (!reduceMotion) return 'full';
  return cue.reduceMotion ?? DEFAULT_REDUCE_MOTION;
}

/** Should a cue fire given the current inputs? Pure. */
export function shouldFireCue(
  cue: ChoreographyCue,
  state: CueState,
  input: CueEvaluationInput,
): boolean {
  if (state.fired) return false; // once-de-dup (also covers re-entry for once:true)
  const t = cue.trigger;
  switch (t.on) {
    case 'enterRegion':
      if (t.once === false && state.fired) return false; // re-armable
      return pointInRegion(input.playerX, input.playerY, t as EnterRegionTrigger);
    case 'event':
      // Event triggers are evaluated by the driver (it listens to EventBus);
      // the model doesn't poll for them — the driver calls fireCue directly.
      return false;
    case 'mapEnter':
      // The driver fires these on scene load (with delayMs); the model
      // doesn't poll — the driver calls fireCue directly.
      return false;
    case 'timeOfDay':
      if (t.once === false && state.fired) return false;
      return input.timePhase === t.phase;
    default:
      return false;
  }
}

/**
 * Evaluate all cues against the current inputs and return the list of beats
 * the driver should execute NOW. Pure — does not mutate state; returns new
 * state + actions.
 *
 * @param cues    the level's authored cues
 * @param states  per-cue runtime state (mutated in place by the driver after)
 * @param input   current game state
 * @returns `{ actions: FiredBeat[], stateChanges: CueState[] }` — the beats to
 *          execute + the state updates to apply (fired flags, emitted beats).
 */
export function evaluateCues(
  cues: ChoreographyCue[],
  states: Map<string, CueState>,
  input: CueEvaluationInput,
): { actions: FiredBeat[]; stateChanges: CueState[] } {
  const actions: FiredBeat[] = [];
  const stateChanges: CueState[] = [];

  for (const cue of cues) {
    let state = states.get(cue.id);
    if (!state) {
      state = createCueState(cue.id);
      states.set(cue.id, state);
    }

    // Check if this cue should fire (region / timeOfDay triggers only —
    // event / mapEnter triggers are fired directly by the driver).
    if (!state.fired && shouldFireCue(cue, state, input)) {
      state.fired = true;
      state.firedAtMs = input.nowMs;
      state.emittedBeats = new Set();
      stateChanges.push(state);
    }

    if (!state.fired) continue;

    // Reduce-motion: 'skip' cues don't emit any beats.
    const mode = effectiveReduceMode(cue, input.reduceMotion);
    if (mode === 'skip') continue;

    const timeline = sortedTimeline(cue.timeline);
    const elapsed = input.nowMs - state.firedAtMs;

    for (let i = 0; i < timeline.length; i += 1) {
      if (state.emittedBeats.has(i)) continue;
      const beat = timeline[i];

      // Reduce-motion 'instant': emit all beats immediately (jump to end).
      if (mode === 'instant') {
        actions.push({ cueId: cue.id, beatIndex: i, beat, progress: 1 });
        state.emittedBeats.add(i);
        stateChanges.push(state);
        continue;
      }

      // Full mode: emit the beat when its atMs has elapsed.
      if (elapsed >= beat.atMs) {
        // Progress for the beat's own ease (overMs). If overMs is 0, progress = 1.
        const overMs = beat.overMs ?? 0;
        const progress = overMs > 0 ? Math.min(1, (elapsed - beat.atMs) / overMs) : 1;
        actions.push({ cueId: cue.id, beatIndex: i, beat, progress });
        state.emittedBeats.add(i);
        stateChanges.push(state);
      }
    }
  }

  return { actions, stateChanges };
}

/**
 * Evaluate an event trigger — called by the driver when an EventBus event
 * fires. Returns the cue ids that should fire (matching the event + payload).
 * Pure.
 */
export function cuesForEvent(
  cues: ChoreographyCue[],
  eventName: string,
  payload: unknown,
): string[] {
  const out: string[] = [];
  for (const cue of cues) {
    if (cue.trigger.on !== 'event') continue;
    const t = cue.trigger;
    if (t.event !== eventName) continue;
    if (!payloadMatches(payload, t.match)) continue;
    out.push(cue.id);
  }
  return out;
}

/**
 * Evaluate a mapEnter trigger — called by the driver on scene load. Returns
 * the cues that should fire (with their delayMs). Pure.
 */
export function cuesForMapEnter(cues: ChoreographyCue[]): { cueId: string; delayMs: number }[] {
  const out: { cueId: string; delayMs: number }[] = [];
  for (const cue of cues) {
    if (cue.trigger.on !== 'mapEnter') continue;
    out.push({ cueId: cue.id, delayMs: cue.trigger.delayMs ?? 0 });
  }
  return out;
}
